export interface DesignStyle {
  name: string;
  description: string;
  image?: string;
  isFree?: boolean;
}

export interface DesignCategory {
  id: string;
  name: string;
  description: string;
  styles: DesignStyle[];
}

const STYLE_IMAGE_BASE = "https://pseuqcfvzaknrbeknpal.supabase.co/storage/v1/object/public/design-images/style-previews";

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const styleImage = (name: string) => `${STYLE_IMAGE_BASE}/${slugify(name)}.png`;

// Pro style
const s = (name: string, description: string, imageSlug?: string): DesignStyle => ({
  name,
  description,
  image: imageSlug ? `${STYLE_IMAGE_BASE}/${imageSlug}.png` : styleImage(name),
});

// Free style (always available to free users)
const f = (name: string, description: string, imageSlug?: string): DesignStyle => ({
  name,
  description,
  image: imageSlug ? `${STYLE_IMAGE_BASE}/${imageSlug}.png` : styleImage(name),
  isFree: true,
});

export const DESIGN_CATEGORIES: DesignCategory[] = [
  {
    id: "classic-global",
    name: "Classic Global Styles",
    description: "Timeless foundational styles that have influenced interior design worldwide",
    styles: [
      f("Modern", "Clean post-1950s lines with functional warmth."),
      f("Rustic", "Raw wood, stone, and cabin warmth."),
      s("Minimalist", "Clean lines, neutral tones, clutter-free calm.", "minimalist"),
      s("Scandinavian", "Nordic coziness with white walls and warmth.", "scandinavian"),
      s("Industrial", "Exposed brick, metal, and raw urban edge.", "industrial"),
      s("Mid-Century Modern", "Retro 60s charm with organic shapes.", "mid-century-modern"),
      s("Art Deco", "Glamorous geometry with bold symmetry.", "art-deco"),
      s("Traditional", "Classic European elegance with ornate details.", "traditional"),
      s("Transitional", "Classic meets modern, timeless comfort.", "transitional"),
      s("Contemporary", "Of-the-moment, evolving, fresh and current."),
    ],
  },
  {
    id: "trend-forward",
    name: "Trend-Forward & Eclectic",
    description: "Creative styles that embrace experimentation and mixing of eras or textures",
    styles: [
      f("Japandi", "Japanese zen meets Scandinavian simplicity.", "japandi"),
      f("Eclectic Fusion", "Curated mix of eras and cultures."),
      s("Bohemian", "Free-spirited with global textures and color.", "bohemian"),
      s("Maximalist", "Bold patterns, vibrant colors, fearless style.", "maximalist"),
      s("Neo-Industrial", "Polished pipes with sleek modern finishes.", "neo-industrial"),
      s("Urban Modern", "Cosmopolitan style for city living.", "urban-modern"),
      s("Memphis", "Playful 80s geometry, funky and bold.", "memphis"),
      s("Wabi-Sabi", "Imperfect beauty with weathered materials."),
      s("Grandmillennial", "Granny chic revival, layered and trending."),
      s("Cottagecore", "Romantic countryside aesthetic with florals."),
    ],
  },
  {
    id: "casual-comfortable",
    name: "Casual & Comfortable",
    description: "Styles that prioritize warmth, ease, and relaxed living",
    styles: [
      f("Coastal", "Breezy blues and beachy natural vibes.", "coastal"),
      f("Modern Farmhouse", "Rustic wood meets cozy modern comfort.", "modern-farmhouse"),
      s("Shabby Chic", "Romantic pastels with vintage charm.", "shabby-chic"),
      s("California Chic", "Relaxed neutrals with natural light.", "california-chic"),
      s("Hygge", "Cozy candlelit Danish warmth and comfort."),
      s("Lagom", "Balanced Swedish 'just enough' living."),
      s("Tropical Resort", "Lush greens, rattan, vacation feel."),
    ],
  },
  {
    id: "luxury-glam",
    name: "Luxury & Glam",
    description: "High-end styles focusing on elegance, bold design, and polished finishes",
    styles: [
      f("Modern Luxe", "Marble, brass, and neutral opulence."),
      f("Parisian Chic", "Effortless French apartment elegance."),
      s("Hollywood Glam", "Velvet, drama, and luxe glamour.", "hollywood-glam"),
      s("Regency", "British elegance with ornate chandeliers.", "regency"),
      s("French Country", "Rustic French beauty with antique charm.", "french-country"),
      s("Art Moderne", "1930s glamour with chrome accents.", "art-moderne"),
      s("Italian Renaissance", "Sculpted grandeur with frescoed walls."),
    ],
  },
  {
    id: "traditional-cultural",
    name: "Traditional & Cultural",
    description: "Heritage-inspired styles drawing from cultural influences with timeless appeal",
    styles: [
      f("Mediterranean", "Sun-kissed terracotta and villa warmth.", "mediterranean"),
      f("Asian Zen", "Harmony and calm with bamboo accents.", "asian-zen"),
      s("Feng Shui", "Balanced energy flow for wellness.", "feng-shui"),
      s("Indo-Colonial", "British-Indian blend with teak and brass.", "indo-colonial"),
      s("Rajasthani Heritage Chic", "Desert royalty with bold mirrorwork.", "rajasthani-heritage-chic"),
      s("Americana", "Cozy nostalgia with warm wood tones.", "americana"),
      s("Moroccan", "Mosaic tiles, lanterns, and jewel tones."),
      s("Tuscan", "Sun-warmed stone and vineyard terracotta."),
      s("Spanish Colonial", "Arched doorways and wrought iron details."),
      s("Japanese Ryokan", "Tatami, shoji screens, and pure zen."),
      s("Nordic Folk", "Painted folk motifs and rural Scandi heritage."),
    ],
  },
  {
    id: "regional-indian",
    name: "Regional Indian Styles",
    description: "Rich diversity of Indian culture reflected through regional design traditions",
    styles: [
      f("Kerala Ethnic Minimalism", "Tranquil temple vibes with brass and cane.", "kerala-ethnic-minimalism"),
      f("Goan Coastal Bungalow", "Breezy Portuguese-tropical coastal charm.", "goan-coastal-bungalow"),
      s("Punjabi Haveli Fusion", "Bold hues with jharokha elegance.", "punjabi-haveli-fusion"),
      s("Bengal Colonial Revival", "Victorian meets Bengali artistic heritage.", "bengal-colonial-revival"),
      s("Mumbai Art Deco Redux", "1930s Bombay geometry, bold and metro.", "mumbai-art-deco-redux"),
      s("Tamil Chettinad", "Athangudi tiles, teak pillars, courtyard homes."),
      s("Gujarati Haveli", "Mirror work, carved jharokhas, vibrant textiles."),
      s("Kashmiri Heritage", "Walnut carving, papier-mâché, pashmina warmth."),
      s("Hyderabadi Nizami", "Indo-Islamic opulence with jaali screens."),
      s("Assamese Bamboo Craft", "Cane, bamboo, tribal Northeast motifs."),
      s("Maharashtrian Wada", "Wooden columns and courtyard-centric layouts."),
    ],
  },
  {
    id: "indian-traditional",
    name: "Indian Traditional",
    description: "Deeply rooted in Indian heritage and craftsmanship",
    styles: [
      f("Indian Traditional", "Vibrant saffron hues with brass accents.", "indian-traditional"),
      f("Mughal Revival", "Domes, arches, and pietra dura inlay."),
      s("South Indian Traditional", "Temple-inspired with earthy wood tones.", "south-indian-traditional"),
      s("Colonial Style", "Indo-British heritage with history.", "colonial-style"),
      s("Marwari Style", "Indo-Persian fusion with intricate details.", "marwari-style"),
      s("Ayurvedic Living", "Wellness-aligned, dosha-balanced spaces."),
      s("Vastu-Aligned Modern", "Contemporary aesthetic following Vastu."),
    ],
  },
  {
    id: "nature-biophilic",
    name: "Nature & Biophilic",
    description: "Plant-forward, organic, sustainable design rooted in nature",
    styles: [
      f("Urban Jungle", "Lush indoor plants in every corner."),
      s("Biophilic Design", "Nature-integrated spaces with living walls."),
      s("Earthy Organic", "Raw textures, earth tones, hand-crafted feel."),
      s("Sustainable Eco-Modern", "Reclaimed materials with modern restraint."),
      s("Desert Modern", "Arid palette with cacti and warm clay."),
    ],
  },
  {
    id: "futuristic-tech",
    name: "Futuristic & Tech",
    description: "Smart-home, sci-fi, and high-tech aesthetics",
    styles: [
      f("Smart Minimalism", "Hidden tech in clean minimalist shells."),
      s("Cyberpunk", "Neon glow, dark surfaces, urban dystopia."),
      s("Space-Age Retro", "1960s sci-fi curves and chrome optimism."),
      s("Parametric Sculptural", "Computational curves and 3D-printed forms."),
    ],
  },
  {
    id: "dark-moody",
    name: "Dark & Moody",
    description: "Bold, atmospheric interiors with dramatic dark palettes",
    styles: [
      f("Dark Academia", "Library-rich interiors with leather and oak."),
      s("Noir Modern", "Black, charcoal, and brass film-noir mood."),
      s("Gothic Revival", "Pointed arches and dramatic Victorian gloom."),
      s("Moody Maximalism", "Deep hues layered with rich textures."),
    ],
  },
  {
    id: "kids-playful",
    name: "Kids & Playful",
    description: "Imaginative, themed spaces designed for children",
    styles: [
      f("Montessori", "Child-scaled, natural materials, open shelves."),
      s("Pastel Dreamland", "Soft pastels and cloud-like cozy textures."),
      s("Whimsical Storybook", "Fairy-tale charm with playful illustrations."),
      s("Adventure Safari", "Jungle motifs and explorer-themed decor."),
    ],
  },
  {
    id: "commercial-hospitality",
    name: "Commercial & Hospitality",
    description: "Cafe, retail, office, and hotel-grade interiors",
    styles: [
      f("Industrial Cafe", "Exposed brick, Edison bulbs, espresso vibes."),
      s("Scandinavian Co-working", "Bright, airy, productivity-focused space."),
      s("Boutique Hotel", "Curated luxury with personal storytelling."),
      s("Speakeasy Lounge", "Velvet booths, low light, prohibition glam."),
      s("Wellness Clinic", "Calming neutrals with clean medical clarity."),
    ],
  },
  {
    id: "wellness-spa",
    name: "Wellness & Spa",
    description: "Restorative, sensory-focused spaces designed for calm",
    styles: [
      f("Meditation Room", "Minimal cushioned space for mindful quiet."),
      s("Spa Sanctuary", "Stone, water, and candlelit serenity."),
      s("Onsen-Inspired", "Japanese hot-spring stone and timber bath."),
      s("Forest Bathing", "Woodland tones with mossy, natural calm."),
    ],
  },
  {
    id: "vintage-retro",
    name: "Vintage & Retro",
    description: "Decade-driven revivals from the 70s through 90s",
    styles: [
      f("70s Retro", "Earth tones, shag rugs, and macramé warmth."),
      s("Vintage Industrial", "Patina metals and salvaged factory pieces."),
      s("80s Memphis Revival", "Bold geometry and confetti-bright palettes."),
      s("90s Grunge Chic", "Plaid, denim, and lived-in alt-rock cool."),
    ],
  },
];

export const getCategoryForStyle = (styleName: string): string | undefined => {
  for (const category of DESIGN_CATEGORIES) {
    if (category.styles.some(st => st.name === styleName)) {
      return category.name;
    }
  }
  return undefined;
};

// Build a fast lookup of free style names
const FREE_STYLE_SET: Set<string> = (() => {
  const set = new Set<string>();
  for (const cat of DESIGN_CATEGORIES) {
    for (const st of cat.styles) {
      if (st.isFree) set.add(st.name);
    }
  }
  return set;
})();

export const isStyleFree = (styleName: string): boolean => FREE_STYLE_SET.has(styleName);

export const getFreeStyleNames = (): string[] => Array.from(FREE_STYLE_SET);
